// import atoms
import { shouldShowPopoverDialogAtom } from 'components/roster-portal/activity-planning/atoms';

// import components
import { Box, Tooltip, Typography, emphasize, lighten } from '@mui/material';
import AbsenceTooltipBody from 'components/roster-portal/activity-planning/components/AbsenceTooltipBody';
import {
  PopoverWarningDialog,
  ScheduleContextMenu,
  ScheduleTooltipBody
} from 'components/roster-portal/activity-planning/components';

// import constants
import { RED } from 'constants/colors';

// import external libraries
import dayjs from 'dayjs';
import { CSSProperties, useContext, useState } from 'react';
import { useRecoilState } from 'recoil';

// import types
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { AppointmentContextProps } from './AppointmentContext.types';

// import utils
import { clipText } from 'utils/misc';

// import context
import { PopOverConfirmationContext } from 'components/roster-portal/activity-planning/PopOverConfirmationContext';
import { StyledExtraSmallIcon } from 'components/shared/Icon/Icon.styles';

const AppointmentContext = ({
  type,
  label,
  startingPoint,
  width,
  schedule,
  absence,
  connectAppointmentGrouping,
  connectSchedule,
  planningEntityIdentifier,
  sx
}: AppointmentContextProps) => {
  const [
    {
      id: popoverOccurenceId,
      scheduleStart: popoverScheduleStart,
      isRouteConnect
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  }); // For contextmenu
  const {
    appointmentGrouping,
    name,
    date,
    employee,
    transportationMode = 'CAR',
    events = [],
    planningEntityId,
    start
  } = schedule || {};
  const transportationIconType: 'car-side' | 'bicycle' =
    transportationMode === 'CAR' ? 'car-side' : 'bicycle';

  const getColorScheme = (): `#${string}` => {
    switch (type) {
      case 'absence':
      case 'unavailability':
        return lighten(RED, 0.9) as `#${string}`;
      case 'availability':
        return '#ACE1DF';
      case 'none':
        return '#F7F7F7';
      case 'schedule':
        return '#D2EBF0';
    }
  };

  const fontStyle: CSSProperties = {
    color: emphasize(getColorScheme(), 0.5),
    fontSize: '10px',
    fontFamily: 'SourceSansPro-Regular',
    whiteSpace: 'nowrap'
  };

  const hasScheduleStart = !!(popoverScheduleStart && start);

  // We compare only the YYYY-MM-DD of the dates, not the time.
  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(start).isValid()
    ? dayjs(start).format('YYYY-MM-DD')
    : '';

  const showPopoverDialog: boolean =
    isRouteConnect &&
    hasScheduleStart &&
    planningEntityId === popoverOccurenceId
      ? popoverScheduleStartDate === currentScheduleStartDate
      : false;

  return (
    <PopoverWarningDialog
      open={showPopoverDialog && !isLoading}
      onClose={() => {
        onClose?.();
        setShouldShowPopoverDialog({ id: '', isRouteConnect: false });
      }}
      onClickAppointment={(editAppointmentType: EditAppointmentType) => {
        onHandleAppointmentConfirmation?.(editAppointmentType);
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '5px',
          zIndex: 0,
          left: startingPoint,
          width,
          height: '35px',
          padding: '4px',
          backgroundColor: getColorScheme(),
          boxSizing: 'border-box',
          borderRadius: '4px',
          boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.075)',
          ...(sx || {})
        }}
      >
        <Tooltip
          disableHoverListener={
            type === 'availability' || type === 'unavailability'
          }
          title={
            type === 'absence' ? (
              <AbsenceTooltipBody type={absence?.type} />
            ) : (
              <ScheduleTooltipBody
                employeeName={employee?.nameInformal}
                appointmentGrouping={appointmentGrouping}
                planningFeatures={employee?.planningFeatures}
              />
            )
          }
          placement="top"
          arrow
          slotProps={{
            popper: {
              sx: {
                zIndex: '0 !important'
              }
            }
          }}
        >
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <span style={fontStyle}>
                {/* We assume 1 character is 8px wide, and only show text if the width is more than 10px */}
                {width >= 10 ? clipText(label, width / 8) : ''}
              </span>

              {(type === 'schedule' || type === 'none') &&
                (schedule?.events || []).length > 1 &&
                width >= 140 && (
                  <Box
                    display="flex"
                    sx={{
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {transportationMode && (
                      <StyledExtraSmallIcon
                        type={transportationIconType}
                        sx={{
                          marginRight: '5px'
                        }}
                      />
                    )}

                    <Typography
                      sx={{
                        fontSize: '10px'
                      }}
                    >
                      {schedule?.metrics?.travelKilometers ?? '-'} km
                    </Typography>
                  </Box>
                )}
            </Box>

            {schedule &&
              (schedule.appointmentGrouping?.entityId ||
                schedule.planningEntityId) &&
              (type === 'schedule' || type === 'none') && (
                <ScheduleContextMenu
                  appointmentGroupingEntityId={appointmentGrouping?.entityId}
                  appointmentGroupingName={name}
                  appointmentGroupingStart={date || ''}
                  onConnectClick={() =>
                    connectAppointmentGrouping?.(
                      appointmentGrouping?.entityId || '',
                      undefined,
                      start
                    )
                  }
                  onConnectScheduleClick={(editAppointmentType) =>
                    connectSchedule?.(
                      planningEntityIdentifier,
                      start,
                      editAppointmentType
                    )
                  }
                  onChangeMousePosition={(x: number, y: number) =>
                    setMousePosition({ x, y })
                  }
                  transportationMode={transportationMode}
                  events={events}
                  employeeId={employee?.id}
                  clickType="rmb"
                  scheduleId={schedule.planningEntityId}
                  planningEntityIdentifier={planningEntityIdentifier}
                  sx={{
                    // Override three-dot button
                    button: {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: 0,
                      opacity: 0
                    }
                  }}
                  isAssignableToEmployee={!!schedule?.isAssignableToEmployee}
                  cursorPosition={{
                    x: mousePosition.x,
                    y: mousePosition.y
                  }}
                />
              )}
          </Box>
        </Tooltip>
      </Box>
    </PopoverWarningDialog>
  );
};

export default AppointmentContext;
